import { useEffect } from "react";
import { gsap } from "gsap";

type BeatIntensity = "subtle" | "balanced" | "dramatic";

const HERO_BEAT_MODE: BeatIntensity = "balanced";

const BEAT_PRESETS: Record<
  BeatIntensity,
  {
    reducedOpacity: number;
    introOpacity: number;
    introScaleFrom: number;
    introDuration: number;
    introStagger: number;
    accentKeyframes: gsap.TweenVars[];
    loopKeyframes: gsap.TweenVars[];
    loopEach: number;
    loopRepeatDelay: number;
  }
> = {
  subtle: {
    reducedOpacity: 0.52,
    introOpacity: 0.68,
    introScaleFrom: 0.62,
    introDuration: 0.2,
    introStagger: 0.16,
    accentKeyframes: [
      { scale: 1.12, opacity: 0.84, duration: 0.12, ease: "power2.out" },
      { scale: 0.98, opacity: 0.55, duration: 0.16, ease: "power2.inOut" },
      { scale: 1, opacity: 0.66, duration: 0.14, ease: "power2.out" },
    ],
    loopKeyframes: [
      { scale: 1.08, opacity: 0.78, duration: 0.18, ease: "sine.out" },
      { scale: 1, opacity: 0.58, duration: 0.22, ease: "sine.in" },
    ],
    loopEach: 0.72,
    loopRepeatDelay: 2.4,
  },
  balanced: {
    reducedOpacity: 0.7,
    introOpacity: 0.86,
    introScaleFrom: 0.35,
    introDuration: 0.25,
    introStagger: 0.12,
    accentKeyframes: [
      { scale: 1.28, opacity: 1, duration: 0.13, ease: "power2.out" },
      { scale: 0.94, opacity: 0.56, duration: 0.16, ease: "power2.in" },
      { scale: 1, opacity: 0.82, duration: 0.16, ease: "power2.out" },
    ],
    loopKeyframes: [
      { scale: 1.18, opacity: 0.98, duration: 0.2, ease: "sine.out" },
      { scale: 1, opacity: 0.76, duration: 0.24, ease: "sine.in" },
    ],
    loopEach: 0.55,
    loopRepeatDelay: 1.8,
  },
  dramatic: {
    reducedOpacity: 0.78,
    introOpacity: 1,
    introScaleFrom: 0.2,
    introDuration: 0.28,
    introStagger: 0.1,
    accentKeyframes: [
      { scale: 1.4, opacity: 1, duration: 0.14, ease: "power2.out" },
      { scale: 0.88, opacity: 0.5, duration: 0.15, ease: "power2.in" },
      { scale: 1.05, opacity: 0.94, duration: 0.15, ease: "power2.out" },
    ],
    loopKeyframes: [
      { scale: 1.25, opacity: 1, duration: 0.19, ease: "sine.out" },
      { scale: 1, opacity: 0.82, duration: 0.23, ease: "sine.in" },
    ],
    loopEach: 0.45,
    loopRepeatDelay: 1.35,
  },
};

export function useGreetingAnimations(
  titleRef: React.RefObject<HTMLHeadingElement>,
  nameRef: React.RefObject<HTMLHeadingElement>,
) {
  useEffect(() => {
    const beatPreset = BEAT_PRESETS[HERO_BEAT_MODE];
    const prefersReducedMotion =
      globalThis.matchMedia?.("(prefers-reduced-motion: reduce)").matches ??
      false;

    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    const titleLetters = titleRef.current?.querySelectorAll(".title-letter");
    const heroWords = titleRef.current?.querySelectorAll(".hero-word");
    const heroBeats = titleRef.current?.querySelectorAll(".hero-beat");
    const nameLetters = nameRef.current?.querySelectorAll(".letter");

    if (prefersReducedMotion) {
      if (titleLetters?.length) {
        gsap.set(titleLetters, { opacity: 1, y: 0 });
      }
      if (heroWords?.length) {
        gsap.set(heroWords, { opacity: 1, y: 0, filter: "blur(0px)" });
      }
      if (heroBeats?.length) {
        gsap.set(heroBeats, { opacity: beatPreset.reducedOpacity, scale: 1 });
      }
      if (nameLetters?.length) {
        gsap.set(nameLetters, { opacity: 1, y: 0, rotationX: 0 });
      }
      return;
    }

    if (titleLetters?.length) {
      tl.fromTo(
        titleLetters,
        {
          opacity: 0,
          y: 30,
          rotateX: -75,
          transformOrigin: "top center",
        },
        {
          opacity: 1,
          y: 0,
          rotateX: 0,
          duration: 0.72,
          stagger: 0.09,
          ease: "power4.out",
        },
      );
    }

    if (heroWords?.length) {
      tl.fromTo(
        heroWords,
        {
          opacity: 0,
          y: 16,
          filter: "blur(8px)",
        },
        {
          opacity: 1,
          y: 0,
          filter: "blur(0px)",
          duration: 0.55,
          stagger: {
            each: 0.032,
            ease: "power2.out",
          },
        },
        "-=0.42",
      );
    }

    if (heroBeats?.length) {
      tl.fromTo(
        heroBeats,
        {
          opacity: 0,
          scale: beatPreset.introScaleFrom,
          filter: "blur(5px)",
        },
        {
          opacity: beatPreset.introOpacity,
          scale: 1,
          filter: "blur(0px)",
          duration: beatPreset.introDuration,
          stagger: beatPreset.introStagger,
          ease: "back.out(3)",
        },
        "-=0.5",
      );

      tl.to(
        heroBeats,
        {
          keyframes: beatPreset.accentKeyframes,
          stagger: beatPreset.introStagger,
        },
        "-=0.3",
      );
    }

    if (nameLetters?.length) {
      tl.fromTo(
        nameLetters,
        {
          opacity: 0,
          y: 42,
          rotationX: -95,
          transformOrigin: "top center",
          filter: "blur(6px)",
        },
        {
          opacity: 1,
          y: 0,
          rotationX: 0,
          filter: "blur(0px)",
          duration: 0.62,
          stagger: { each: 0.02, ease: "power2.out" },
          ease: "expo.out",
        },
        "-=0.22",
      );
    }

    let wordLoop: gsap.core.Tween | null = null;
    let beatLoop: gsap.core.Tween | null = null;
    if (heroWords?.length) {
      wordLoop = gsap.to(heroWords, {
        yPercent: -3,
        duration: 2.8,
        ease: "sine.inOut",
        stagger: {
          each: 0.11,
          from: "center",
          yoyo: true,
          repeat: -1,
        },
        yoyo: true,
        repeat: -1,
      });
    }

    if (heroBeats?.length) {
      beatLoop = gsap.to(heroBeats, {
        keyframes: beatPreset.loopKeyframes,
        stagger: {
          each: beatPreset.loopEach,
          repeat: -1,
        },
        repeat: -1,
        repeatDelay: beatPreset.loopRepeatDelay,
      });
    }

    return () => {
      tl.kill();
      wordLoop?.kill();
      beatLoop?.kill();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
